import { type CertainPartial, type TbJsonConfig } from '../../domain';
import { loadLibScripts } from '../../loadAsset/loadLibScripts';
import { loadLink } from '../../loadAsset/loadLink';
import {
    type ComponentCache,
    type ComponentMeta,
    type Editor,
    getExportModuleVariable,
    type Lock,
    type MetaCache
} from '../providerDomain';
import { toTag } from '../utils/tag';
import { traverse } from '../utils/traverse';

import { type RegistryProviderOptions } from './options';

export const loadEditor = (componentMeta: ComponentMeta, editorCache: ComponentCache<Editor | undefined>) => {
    const editorType = `editor-${componentMeta.type}`;
    const tag = toTag(editorType, componentMeta.version);

    editorCache.maybeAddModule(tag, async () => {
        const exportVariable = getExportModuleVariable(editorType, componentMeta.version);

        await loadLibScripts();
        await Promise.all(componentMeta.editorLinks.map(loadLink));

        return (window as any)[exportVariable] as Editor | undefined;
    });

    return editorCache.getModule(tag);
};

export const getAllEditors = async ({
    registryUrl,
    registryPrereleaseTag,
    metaCache,
    editorCache
}: RegistryProviderOptions) => {
    const normalizedUrl = registryUrl.replace(/\/$/g, '');
    const queryParams = registryPrereleaseTag ? `?prerelease=${registryPrereleaseTag}` : '';

    const response = await fetch(`${normalizedUrl}/latest${queryParams}`);
    const data: { found: ComponentMeta[] } = await response.json();

    const isValidLink = (link: string) =>
        link.startsWith('http://localhost') || link.startsWith('https://tlkfrontprod.azureedge.net');

    const filteredFount = (data.found || []).filter(
        (meta) => meta.editorLinks.every(isValidLink) && meta.links.every(isValidLink)
    );

    const componentsMeta = filteredFount.map((componentMeta) => {
        metaCache.maybeAdd(toTag(componentMeta.type, componentMeta.version), Promise.resolve(componentMeta));

        return componentMeta;
    });

    const editorList = await Promise.all(
        componentsMeta.map(async (meta) => {
            const [editor] = await Promise.all([loadEditor(meta, editorCache)]);

            return {
                type: meta.type,
                code: editor
            };
        })
    );

    const editorMap: {
        [type: string]: CertainPartial<Editor, 'schema'>;
    } = {};

    for (const editor of editorList) {
        if (editor.code) {
            editorMap[editor.type] = { ...editor.code };
        }
    }

    return editorMap;
};

const isTbObject = (value: unknown): value is { type: string } => {
    return Boolean(typeof value === 'object' && value && 'type' in value);
};

export const getLock = async (config: TbJsonConfig, metaCache: MetaCache) => {
    const coreMeta = await metaCache.getByType('core');

    const lock: Lock = {};

    if (!coreMeta) {
        return lock;
    }

    lock.core = coreMeta.version;

    const additions: Array<Promise<void>> = [];

    const addType = (type: string) => {
        additions.push(
            (async () => {
                const meta = await metaCache.getByType(type);

                if (meta) {
                    lock[type] = meta.version;
                }
            })()
        );
    };

    traverse(config, (value) => {
        if (isTbObject(value)) {
            addType(value.type);
        }
    });

    await Promise.all(additions);

    return lock;
};
